// Two-tab compose body — Write / Preview — with a tiny formatting toolbar.
// Wraps a plain <textarea> so autoscroll, keyboard shortcuts, and paste all
// behave like normal text areas. Preview is the sanitised HTML that will
// render on the article page.

import { useRef, useState } from 'react';
import { renderMarkdown } from '../lib/markdown';

interface Props {
    value:    string;
    onChange: (v: string) => void;
    placeholder?: string;
    rows?: number;
    required?: boolean;
}

type Tab = 'write' | 'preview';

export function MarkdownEditor({ value, onChange, placeholder, rows = 5, required }: Props) {
    const [tab, setTab] = useState<Tab>('write');
    const taRef = useRef<HTMLTextAreaElement>(null);

    // Wrap the current selection with `before`/`after`. If nothing is
    // selected, we insert both and drop the cursor between them.
    function wrap(before: string, after: string = before) {
        const ta = taRef.current;
        if (!ta) return;
        const s  = ta.selectionStart;
        const e  = ta.selectionEnd;
        const sel = value.slice(s, e);
        const next = value.slice(0, s) + before + sel + after + value.slice(e);
        onChange(next);
        // Re-focus + place cursor after the inserted content.
        requestAnimationFrame(() => {
            ta.focus();
            const pos = s + before.length + sel.length + (sel ? after.length : 0);
            ta.setSelectionRange(pos, pos);
        });
    }
    function prefixLines(prefix: string) {
        const ta = taRef.current;
        if (!ta) return;
        const s = ta.selectionStart;
        const e = ta.selectionEnd;
        const before = value.slice(0, s);
        const after  = value.slice(e);
        const lineStart = before.lastIndexOf('\n') + 1;
        const chunk     = value.slice(lineStart, e);
        const prefixed  = chunk.split('\n').map(l => prefix + l).join('\n');
        onChange(before.slice(0, lineStart) + prefixed + after);
        requestAnimationFrame(() => ta.focus());
    }

    return (
        <div className="md-editor">
            <div className="md-toolbar">
                <div className="md-tabs">
                    <button type="button"
                            className={'md-tab' + (tab === 'write'   ? ' is-active' : '')}
                            onClick={() => setTab('write')}>Write</button>
                    <button type="button"
                            className={'md-tab' + (tab === 'preview' ? ' is-active' : '')}
                            onClick={() => setTab('preview')}>Preview</button>
                </div>
                {tab === 'write' && (
                    <div className="md-buttons">
                        <button type="button" title="Bold (**text**)"   onClick={() => wrap('**')}>B</button>
                        <button type="button" title="Italic (*text*)"   onClick={() => wrap('*')} style={{ fontStyle: 'italic' }}>I</button>
                        <button type="button" title="Link"              onClick={() => wrap('[', '](https://)')}>🔗</button>
                        <button type="button" title="Bullet list"       onClick={() => prefixLines('- ')}>• list</button>
                        <button type="button" title="Numbered list"     onClick={() => prefixLines('1. ')}>1. list</button>
                        <button type="button" title="Quote"             onClick={() => prefixLines('> ')}>❝</button>
                        <button type="button" title="Inline code (`x`)" onClick={() => wrap('`')}>{'</>'}</button>
                    </div>
                )}
            </div>

            {tab === 'write' ? (
                <textarea
                    ref={taRef}
                    className="md-textarea"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={rows}
                    required={required}
                />
            ) : (
                <div
                    className="md-preview"
                    dangerouslySetInnerHTML={{
                        __html: value.trim()
                            ? renderMarkdown(value)
                            : '<p style="color:#999;font-style:italic">Nothing to preview yet.</p>',
                    }}
                />
            )}
            <em className="md-hint">
                Markdown supported: <code>**bold**</code>, <code>*italic*</code>, <code>[link](url)</code>,
                lists, <code>&gt; quote</code>, <code>`code`</code>.
            </em>
        </div>
    );
}
